// ═══════════════════════════════════════════════════════════════════════════════════════════
// WaSentMessageDialog — "צפייה בהודעה" for ONE sent WhatsApp message (11-sendsearch §7/§8).
//
// The phone is the LIVE `WhatsappMobilePreview` (the WhatsApp editor's and SmartSend's), fed through
// the SAME helpers SmartSend's "כך תיראה ההודעה" uses (`templateShape.ts` + `getTemplateTextWithVariable`).
// Read-only imports; nothing under SmartSend or Whatsapp is modified, and there is no second renderer.
//
// WHAT THE PHONE IS, AND WHAT IT IS NOT — stated on screen, not only here:
//   • the VALUES are the recorded ones (`WhatsAppSendText.TemplateVariables`, §5);
//   • the TEXT is the template body AS IT IS STORED TODAY. SmartSend sends store no rendered text
//     (`TextMessage` is NULL — DataSources_WhatsApp_Send_Stage:421-422), so an edited template would
//     show today's wording. The note under the phone says exactly that
//     ("נוסח התבנית כפי שהוא שמור היום, עם הערכים שנשלחו"), so the operator never reads this as a
//     screenshot of the recipient's phone.
//
// The message is fetched LAZILY (one request per deliberate view) into its own slice slot with a
// stale guard, and cleared on close so the next person's dialog never paints this one's message.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DateFormats } from '../../../helpers/Constants';
import InlineBanner from '../../SmartSend/components/InlineBanner';
import WhatsappMobilePreview from '../../Whatsapp/Editor/Components/WhatsappMobilePreview';
import { getTemplateTextWithVariable } from '../../Whatsapp/Common';
import { fillPreviewValues, mediaFileData, previewTemplateData } from '../../SmartSend/whatsapp/templateShape';
import { WaTemplateItem } from '../../../Models/DataSources/SmartSendWhatsApp';
import { eSendChannel } from '../../../Models/DataSources/SmartSend';
import {
    SS,
    SendSearchWaMessage,
    StateTone,
    camelCaseState,
    deliveryTone,
    displayPhone,
    engagementTone,
    toChannelAttempt,
} from '../../../Models/DataSources/SendSearch';
import { getSendSearchWaMessage, clearSendSearchWaMessage } from '../../../redux/reducers/sendSearchSlice';

const TONE_COLOR: { [k in StateTone]: string } = {
    ok: '#067647', bad: '#B42318', warn: '#B54708', muted: '#5b6b7b',
};

// Unique bubble DOM id: `WhatsappMobilePreview` measures its bubble with getElementById, and the
// editor / SmartSend phones use their own ids (WhatsAppPhonePreview.tsx:29-32).
const PREVIEW_DOM_ID = 'sendsearch-wa-sent-preview';
// The phone's width — the owner's own measure for SmartSend's phone (WhatsAppPhonePreview.tsx:34-38).
const PHONE_WIDTH = 324;

// The same phone block as SmartSend's (WhatsAppPhonePreview.tsx:43, :53-59): centred, PHONE_WIDTH
// wide, and the bubble filling the chat (owner, stage 2026-09-28: "the green inside to 100%") instead
// of the editor's 85% cap (whatsappStyles.js:412). Scoped to this dialog; the editor keeps its 85%.
const usePhoneStyles = makeStyles({
    phone: {
        width: '100%', maxWidth: PHONE_WIDTH, marginLeft: 'auto', marginRight: 'auto',
        '& .sent': { maxWidth: '100%' },
    },
});

interface Props {
    open: boolean;
    onClose: () => void;
    // `SendSearchRow.WaMessageID` (= ApiWhatsappSendLogs.ID). The button that opens this dialog is
    // disabled unless it is > 0, so the dialog never asks for a message that does not exist.
    messageId: number;
    // WHO, from the grid row — the message itself carries only the number it went to.
    recipientName: string;
    // The app-wide sheet `WhatsappMobilePreview` is styled by (see SendSearchPanel `classes`).
    appClasses?: any;
}

const WaSentMessageDialog: React.FC<Props> = ({ open, onClose, messageId, recipientName, appClasses }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const phoneClasses = usePhoneStyles();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);
    const msg: SendSearchWaMessage | null = useSelector((s: any) => s.sendSearch?.waMessage ?? null);
    const loading: boolean = useSelector((s: any) => !!s.sendSearch?.waMessageLoading);
    const error: string | null = useSelector((s: any) => s.sendSearch?.waMessageError ?? null);
    const loadedId: number | null = useSelector((s: any) => s.sendSearch?.waMessageId ?? null);

    useEffect(() => {
        if (open && messageId > 0) dispatch(getSendSearchWaMessage(messageId));
    }, [dispatch, open, messageId]);

    // Closing drops the message from the store, so reopening on another person never flashes this one.
    const close = () => {
        dispatch(clearSendSearchWaMessage());
        onClose();
    };

    // The ONE slot in the store must belong to THIS message before anything is painted from it.
    const mine: SendSearchWaMessage | null = msg && loadedId === messageId ? msg : null;
    // QA BUG-03 — the number the message went to, 972… as 0… (`displayPhone`, approved 4.4). Printed once:
    // not again when it IS the WHO (an agent with no name and no e-mail — RollupDrawer `who`), the
    // roster's own `phoneIsWho` rule.
    const toNumber = mine ? displayPhone(mine.ToNumber) : null;
    const showToNumber = !!toNumber && toNumber !== recipientName;

    // The body with the RECORDED values. `fillPreviewValues` gives every `{{n}}` a value — a missing
    // one keeps its own token rather than printing "undefined" inside the bubble (templateShape.ts:32-41).
    const renderedText = useMemo(() => {
        if (!mine || !mine.BodyText) return '';
        const values = fillPreviewValues(mine.PlaceholderCount ?? 0, mine.Variables ?? {});
        return getTemplateTextWithVariable(mine.BodyText, values as any) ?? '';
    }, [mine]);

    // Media: the message's own (WhatsAppSendText.MediaUrl, else the template's — the server already
    // chose, §7). Through `mediaFileData` so the URL-extension rule stays the one SmartSend uses.
    const fileData = useMemo(() => mediaFileData(mine
        ? ({ HasMedia: !!mine.MediaUrl, MediaUrl: mine.MediaUrl, MediaType: mine.MediaType } as WaTemplateItem)
        : null), [mine]);

    // The status, NARROWED — same single narrowing point as every other state on this screen (D10).
    const a = mine
        ? toChannelAttempt({
            Channel: eSendChannel.WHATSAPP,
            DeliveryState: mine.DeliveryState,
            EngagementState: mine.EngagementState ?? 'None',
            EvidenceAt: mine.ReadAt ?? mine.DeliveredAt ?? mine.SentAt,
        })
        : null;
    const hasEngagement = !!a && a.EngagementState !== 'None';
    const tone: StateTone = !a ? 'muted' : (hasEngagement ? engagementTone(a.EngagementState) : deliveryTone(a.DeliveryState));
    const statusWords = !a ? '' : (hasEngagement
        ? `${t(`${SS}delivery.${camelCaseState(a.DeliveryState)}`)} · ${t(`${SS}engagement.${camelCaseState(a.EngagementState)}`)}`
        : t(`${SS}delivery.${camelCaseState(a.DeliveryState)}`));

    // The envelope's own reason, in words. PERMISSION_DENIED / NOT_FOUND / WA_VIEW_NOT_AVAILABLE are
    // the three the API names (§7); anything else is a failed load.
    const errorText = error === 'PERMISSION_DENIED'
        ? t(`${SS}error.permissionDenied`)
        : (error === 'NOT_FOUND'
            ? t(`${SS}wa.dialog.notFound`)
            : (error === 'WA_VIEW_NOT_AVAILABLE' ? t(`${SS}wa.dialog.notAvailable`) : t(`${SS}wa.dialog.loadFailed`)));

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth="xs"
            fullWidth
            // MUI portals the Dialog outside App's inner <div dir>, and <html dir> is stuck "ltr" —
            // the same reason WaPhonePreviewDialog.tsx:51 sets it.
            dir={isRTL ? 'rtl' : 'ltr'}
            aria-labelledby="sendsearch-wa-sent-title"
        >
            <DialogTitle id="sendsearch-wa-sent-title" disableTypography style={{ paddingBottom: 8 }}>
                <Typography component="div" style={{ fontSize: 17, fontWeight: 800 }}>
                    {t(`${SS}wa.dialog.title`)}
                </Typography>
                {/* WHO / NUMBER / TIME / STATUS — the facts that make this a record. The number and
                    the time are isolated LTR runs (a phone and a 24h clock reorder under RTL). */}
                <Box style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 4, fontSize: 13, color: '#5b6b7b' }}>
                    <bdi style={{ fontWeight: 700, color: '#3b4754' }}>{recipientName}</bdi>
                    {showToNumber && (
                        <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{toNumber}</span>
                    )}
                    {mine && (
                        <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                            {mine.SentAt ? moment(mine.SentAt).format(DateFormats.DATE_TIME_24) : '—'}
                        </span>
                    )}
                </Box>
                {mine && (
                    <Box style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                        <Typography component="span" style={{ fontSize: 14, fontWeight: tone === 'muted' ? 400 : 700, color: TONE_COLOR[tone] }}>
                            {statusWords}
                        </Typography>
                        {mine.IsTest && (
                            <Chip size="small" variant="outlined" label={t(`${SS}wa.dialog.test`)} style={{ height: 20, fontSize: 11.5 }} />
                        )}
                        {!!mine.TemplateName && (
                            <Typography component="span" style={{ fontSize: 12.5, color: '#5b6b7b' }}>
                                <bdi>{mine.TemplateName}</bdi>
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogTitle>

            <DialogContent style={{ paddingTop: 0 }}>
                {loading && (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {!loading && !!error && (
                    <InlineBanner severity={error === 'PERMISSION_DENIED' ? 'warning' : 'error'} role="alert" title={errorText} />
                )}
                {!loading && !error && mine && (
                    <>
                        {mine.TemplateDeleted && (
                            <Box style={{ marginBottom: 8 }}>
                                <InlineBanner severity="info" role="status" title={t(`${SS}wa.dialog.templateDeleted`)} />
                            </Box>
                        )}
                        {renderedText ? (
                            <Box className={phoneClasses.phone}>
                                <WhatsappMobilePreview
                                    classes={appClasses ?? {}}
                                    templateData={previewTemplateData(renderedText)}
                                    buttonType=""
                                    fileData={fileData}
                                    previewId={PREVIEW_DOM_ID}
                                />
                            </Box>
                        ) : (
                            // No body to show. Never an empty bubble — it would read as "the message
                            // was blank".
                            <Typography component="div" style={{ fontSize: 13.5, color: '#5b6b7b', padding: '12px 0' }}>
                                {t(`${SS}wa.dialog.noBody`)}
                            </Typography>
                        )}
                        {/* The honesty note — on screen, under the phone, not in a tooltip. */}
                        <Typography component="div" style={{ fontSize: 12, color: '#5b6b7b', marginTop: 10 }}>
                            {t(`${SS}wa.dialog.note`)}
                        </Typography>
                    </>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '12px 24px', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={close}>{t(`${SS}wa.dialog.close`)}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default WaSentMessageDialog;
